import { startTransition, useCallback, useMemo } from "react";
import { useLocation, useRouter } from "@tanstack/react-router";
import {
  renderQueryString,
  unstable_createAdapterProvider as createAdapterProvider,
  type unstable_UpdateUrlFunction as UpdateUrlFunction,
} from "nuqs/adapters/custom";

/**
 * Percent-encodes raw pipes in the query segment of a URL — single encoding.
 *
 * Contract (task 0042 A06 proven defect, task 0231 Q9a ruling): a raw `|`
 * committed by nuqs makes Tomcat answer 400 on reload. nuqs's
 * `encodeQueryValue` escapes every `%` and never emits `%7C` itself, so a
 * value-serializer CAN'T produce a single-encoded pipe in the address bar
 * (serializer-emitted `%7C` double-encodes to `%257C`; the probe evidence is
 * in `tasks/0231-url-state-architecture-artifacts/probe-pipe-encoder-variant.md`).
 * The adapter's `updateUrl` — which builds the final navigate target — is the
 * only single-encoding seam. This encoder post-processes the rendered query
 * string exactly like the deleted `searchUrlSanitizer` history patch did:
 * path, hash, existing `%7C` escapes, param order, and every other character
 * stay byte-identical.
 */
export function encodePipesInQuery(url: string): string {
  const queryIndex = url.indexOf("?");
  if (queryIndex === -1) {
    return url;
  }
  const base = url.slice(0, queryIndex);
  const rest = url.slice(queryIndex + 1);
  const hashIndex = rest.indexOf("#");
  const query = hashIndex === -1 ? rest : rest.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : rest.slice(hashIndex);
  return `${base}?${query.replace(/\|/g, "%7C")}${hash}`;
}

/**
 * The storefront's nuqs adapter (task 0231 Q9a): a minimal diff of nuqs
 * 2.8.9's `nuqs/adapters/tanstack-router` hook. Deltas from stock, all
 * intentional: (1) the navigate target encodes raw pipes in the rendered
 * query string via `href:` + `encodePipesInQuery` — see the updateUrl
 * comment; (2) the navigate carries a `.catch` that logs instead of
 * surfacing an unhandled rejection (same pattern as the hydration
 * hash-strip navigate in useSearchPageViewModel); (3) the stock hook's
 * inert `from: "/"` is dropped (it only feeds a dev-only warning for
 * relative paths, and the target is always absolute here); (4) array and
 * scalar values go through `String(item)` coercion before URLSearchParams
 * (semantically identical — URLSearchParams stringifies anyway). When nuqs
 * updates, re-diff against
 * `node_modules/nuqs/dist/adapters/tanstack-router.js` and keep this copy
 * minimal. The pipe-encoding integration test in
 * `pages/SearchPage/SearchPage.cutover.test.tsx` gates the behavior.
 */
function useStorefrontNuqsAdapter(watchKeys: string[]) {
  const pathname = useLocation({ select: (state) => state.pathname });
  const search = useLocation({
    select: (state) => Object.fromEntries(Object.entries(state.search).filter(([key]) => watchKeys.includes(key))),
  });
  const { navigate } = useRouter();
  const watchKey = watchKeys.join(",");
  const searchParams = useMemo(
    () =>
      new URLSearchParams(
        Object.entries(search).flatMap(([key, value]): string[][] => {
          if (Array.isArray(value)) {
            return value.map((item) => [key, String(item)]);
          }
          if (typeof value === "object" && value !== null) {
            return [[key, JSON.stringify(value)]];
          }
          return [[key, String(value)]];
        }),
      ),
    // The serialized watch-key string, not the array identity (nuqs's own dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirrors the stock adapter
    [search, watchKey],
  );
  const updateUrl = useCallback<UpdateUrlFunction>(
    (search, options) => {
      startTransition(() => {
        navigate({
          // href (not to): a `to` string with a query travels as a path
          // segment through decodePath/decodeURI, which collapses every
          // pipe escape back to a raw `|` (probe evidence in the task
          // artifacts dir). An href navigation instead re-parses the query
          // into the search object and re-serializes it through the router's
          // stringifySearch (qss encode, URLSearchParams-based, with
          // JSON-shaped values round-tripped), which single-encodes the
          // pipe as %7C — together with encodePipesInQuery this keeps the
          // wire exactly on the legacy grammar (filter=FV%3Abrand%3AA%7CB).
          // Path references: on the EXECUTING router the href parse sits in
          // buildAndCommitLocation (the @tanstack/router-core 1.171.13
          // nested under @tanstack/react-router@1.170.16,
          // dist/esm/router.js:432-440). NOTE the dual-copy trap: a hoisted
          // router-core also exists in node_modules — read the NESTED one
          // when verifying router behavior.
          href: pathname + encodePipesInQuery(renderQueryString(search)),
          replace: options.history === "replace",
          resetScroll: options.scroll,
          hash: (prevHash) => prevHash ?? "",
          state: (state) => state,
        }).catch((error: unknown) => {
          // A failed URL update logs instead of surfacing as an unhandled
          // rejection during a nuqs write (same pattern as the hydration
          // hash-strip navigate in useSearchPageViewModel).
          console.error("nuqs URL update navigation failed", error);
        });
      });
    },
    [navigate, pathname],
  );
  return { searchParams, updateUrl, rateLimitFactor: 1 };
}

/** Mounted once in the root route component around the shell/Outlet (task 0231 D1/Q9a). */
export const StorefrontNuqsAdapter = createAdapterProvider(useStorefrontNuqsAdapter);
